import React from "react";

import {IDecorParamTypeDescriptor} from "../../database/interfaces/paramScript/IParamTypeDescriptor";
import IBoardData from "../../database/interfaces/IBoardData";
import {DatabaseContext} from "../../database/DatabaseHandler";

import BoardContent from "./BoardContent";

import { SIDE_KEYS, ALL_EDGE_KEYS, VISIBLE_EDGE_KEY, shownSideTokens, unavailableSideFlags } from "./edgeKeys";
import {
    ZERO_GUID,
    getEdgeIds,
    getPreferredThickness,
    pickBestEdge,
    applyAutoEdgeWithCompatCheck,
    replaceEdgesBySameDecor,
    replaceCompatEdgesToNewDecor,
} from "./edgeAutoSelect";
import { isEdgeAvailable } from "./edgeAvailability";

/**
 * A Decor kártya BOARD-oldali tartalma (board + duplung sor) és a hozzá tartozó
 * handlerek — a régi DecorCardContent-ből VÁLTOZATLAN logikával kiemelve.
 * Az él-oldal (EdgeListContent) ettől teljesen külön komponens; a kettő
 * KIZÁRÓLAG a paramValue-n (onUpdateParamValue) keresztül kommunikál.
 */
interface IBoardDecorCardContentProps {
    typeDescriptor: IDecorParamTypeDescriptor;
    paramValue: any;
    boardProduct: IBoardData;
    duplungProduct: IBoardData | null;
    onUpdateParamValue: (value: any) => void;
}

const BoardDecorCardContent: React.FC<IBoardDecorCardContentProps> = ({
                                                                          typeDescriptor, paramValue, boardProduct, duplungProduct, onUpdateParamValue
                                                                      }) => {
    const dataContext = React.useContext(DatabaseContext);

    // Worktop boardnál a Top ("front") él nem választható, ezért a board-thumbnailen sem
    // jelölünk felső élzárást. Nem-worktopnál allowedSides = mind a 4 oldal → változatlan.
    const allowedSides = shownSideTokens(typeDescriptor.edge?.sideLabels, !!boardProduct?.IsWorkTop);
    // A nem élzárható oldal(ak) szaggatott kerettel — így látszik, hogy ott nincs is Top/él, nem csak
    // "nincs kiválasztva". Nem-worktopnál minden oldal választható → nincs szaggatott keret.
    const unavailableEdges = unavailableSideFlags(allowedSides);

    const handleDecorChange = (newId: string | null, isDuplung: boolean) => {
        if (newId == null) return;

        const newBoard = dataContext.boardData?.findByID(newId);
        console.log("Board changed:", newId, "CompatibleEdgings:", newBoard?.CompatibleEdgings);
        const newScript = { ...paramValue };
        if (isDuplung) {
            newScript.DuplungProduct = newId;

            const newDuplungBoard = dataContext.boardData?.findByID(newId);
            const requiredWidth =
                (boardProduct?.SheetThickness ?? 0) +
                (newDuplungBoard?.SheetThickness ?? 0);

            if (typeDescriptor.edge && boardProduct?.CompatibleEdgings?.length) {
                const autoEdgeId = pickBestEdge(
                    dataContext.edgeData,
                    boardProduct.CompatibleEdgings,
                    requiredWidth,
                    null,
                    getPreferredThickness(dataContext.edgeData, newScript)
                );

                if (autoEdgeId) {
                    applyAutoEdgeWithCompatCheck(newScript, autoEdgeId, boardProduct.CompatibleEdgings);
                }
            }
        } else {
            newScript.BoardProduct = newId;
            // If new board is not edge-bandable, clear all edges (oldalak + láthatóság)
            if (newBoard?.EdgeBandable != 1) {
                ALL_EDGE_KEYS.forEach(key => { delete newScript[key]; });
            } else if (typeDescriptor.edge && newBoard?.CompatibleEdgings?.length) {
                const requiredWidth =
                    (newBoard?.SheetThickness ?? 0) +
                    (duplungProduct ? duplungProduct.SheetThickness : 0);

                const maxWidth = duplungProduct ? null : requiredWidth + 12;
                // Fallback CSAK elérhető (Status = 1) kompatibilis élre — a CompatibleEdgings[0]
                // Status ≠ 1 lehet, azt nem állítjuk be (különben üresnek látszana a selector).
                const autoEdgeId = pickBestEdge(
                    dataContext.edgeData,
                    newBoard.CompatibleEdgings,
                    requiredWidth,
                    maxWidth,
                    getPreferredThickness(dataContext.edgeData, newScript)
                ) ?? newBoard.CompatibleEdgings.find(id => {
                    const e = dataContext.edgeData?.findByID(id);
                    return !!e && isEdgeAvailable(e);
                });

                // Ha egyetlen elérhető kompatibilis él sincs, nem állítunk be semmit (autoEdgeId undefined).
                if (autoEdgeId) {
                    const hasAnyEdge = getEdgeIds(newScript).some(e => e && e !== ZERO_GUID);
                    if (hasAnyEdge) {
                        // A régi board dekorjához tartozó éleket az ÚJ board dekorjára cseréljük,
                        // MINDEGYIK saját vastagságát megtartva (pl. 2mm→2mm, 0.8mm→0.8mm).
                        // Idegen (kézzel beállított) éleket békén hagyjuk.
                        replaceCompatEdgesToNewDecor(
                            dataContext.edgeData,
                            newScript,
                            boardProduct?.CompatibleEdgings || [],
                            newBoard.CompatibleEdgings,
                            autoEdgeId,
                            requiredWidth,
                            maxWidth
                        );
                    } else {
                        // ÜRES kártya: az alap él-cél a LÁTHATÓ, nem a négy oldal. Ahol a kártya ki sem
                        // rakja a láthatóság-gombokat (front, hátfal, munkalap, lábazat), ott marad a
                        // négy oldal — különben olyan tokent kapna, amihez nincs is gomb.
                        const targets: readonly string[] = typeDescriptor.edge?.visibilityLabels
                            ? [VISIBLE_EDGE_KEY]
                            : SIDE_KEYS;
                        targets.forEach(key => { newScript[key] = autoEdgeId; });
                    }
                }
            }
        }
        onUpdateParamValue(newScript);
    };

    const handlePatternDirectionChange = (newValue: string, isDuplung: boolean) => {
        const newScript = { ...paramValue };
        if (isDuplung) {
            newScript.DuplungPatternDirection = newValue;
        } else {
            newScript.BoardPatternDirection = newValue;
        }
        onUpdateParamValue(newScript);
    };

    const handleDuplungChange = (newValue: boolean) => {
        const newScript = { ...paramValue };
        if (newValue) {
            newScript.DuplungProduct = newScript.BoardProduct;
            newScript.DuplungPatternDirection = newScript.BoardPatternDirection;
            newScript.DuplungFixing = "Gluing";

            if (typeDescriptor.edge && boardProduct != null && boardProduct.CompatibleEdgings) {
                const requiredWidth = (boardProduct?.SheetThickness ?? 0) * 2;
                const edgesToPickFrom = boardProduct.CompatibleEdgings.map((edgeID) => {
                    return dataContext.edgeData?.findByID(edgeID);
                }).filter((edge) => {
                    return edge && isEdgeAvailable(edge) && edge.Width && edge.Width >= requiredWidth;
                });
                const edgeToPick = edgesToPickFrom.length !== 0 ? edgesToPickFrom[0] : null;

                console.log("FILTER CHECK", {
                    edgeFilters: typeDescriptor.edge?.filters,
                    pickedThickness: edgeToPick?.Thickness,
                    pickedWidth: edgeToPick?.Width,
                    pickedOriented: edgeToPick?.Oriented,
                });

                newScript.TopEdgeProduct = edgeToPick?.ID;
                newScript.LeftEdgeProduct = edgeToPick?.ID;
                newScript.RightEdgeProduct = edgeToPick?.ID;
                newScript.BottomEdgeProduct = edgeToPick?.ID;
            }

        } else {
            delete newScript.DuplungProduct;
            delete newScript.DuplungPatternDirection;
            delete newScript.DuplungFixing;

            if (typeDescriptor.edge) {
                const requiredWidth = boardProduct?.SheetThickness ?? 0;
                replaceEdgesBySameDecor(dataContext.edgeData, newScript, requiredWidth, requiredWidth + 12);
            }
        }

        onUpdateParamValue(newScript);
    };

    if (!typeDescriptor.board) return null;

    return (
        <>
            <BoardContent
                board={boardProduct}
                format={typeDescriptor.board.format || null}
                filters={typeDescriptor.board.filters || null}
                patternDirection={paramValue.BoardPatternDirection}
                duplungChecked={paramValue.DuplungProduct}
                tooltip={typeDescriptor.board.tooltip || ""}
                isDuplung={false}
                editable={typeDescriptor.editable === true && typeDescriptor.board.editable === true}
                readOnly={typeDescriptor.readOnly === true || typeDescriptor.board.readOnly === true}
                notFound={paramValue.BoardNotFound === true}
                showPatternDirection={typeDescriptor.board.patternDirection}
                edges={{
                    top: allowedSides.includes("top") && paramValue.TopEdgeProduct && paramValue.TopEdgeProduct !== ZERO_GUID,
                    bottom: allowedSides.includes("bottom") && paramValue.BottomEdgeProduct && paramValue.BottomEdgeProduct !== ZERO_GUID,
                    left: allowedSides.includes("left") && paramValue.LeftEdgeProduct && paramValue.LeftEdgeProduct !== ZERO_GUID,
                    right: allowedSides.includes("right") && paramValue.RightEdgeProduct && paramValue.RightEdgeProduct !== ZERO_GUID,
                }}
                unavailableEdges={unavailableEdges}
                onDecorChange={(newValue) => handleDecorChange(newValue, false)}
                onPatternDirectionChange={(newValue) => handlePatternDirectionChange(newValue, false)}
                onDuplungChange={handleDuplungChange}
            />
            {duplungProduct && (
                <BoardContent
                    board={duplungProduct}
                    format={typeDescriptor.board.format || null}
                    filters={typeDescriptor.board.filters || null}
                    patternDirection={paramValue.DuplungPatternDirection}
                    duplungChecked={paramValue.DuplungProduct}
                    tooltip={typeDescriptor.board.tooltip || ""}
                    isDuplung={true}
                    editable={typeDescriptor.editable === true && typeDescriptor.board.editable === true}
                    readOnly={typeDescriptor.readOnly === true || typeDescriptor.board.readOnly === true}
                    notFound={paramValue.DuplungNotFound === true}
                    showPatternDirection={typeDescriptor.board.patternDirection}
                    onDecorChange={(newValue) => handleDecorChange(newValue, true)}
                    onPatternDirectionChange={(newValue) => handlePatternDirectionChange(newValue, true)}
                />
            )}
        </>
    );
};

export default BoardDecorCardContent;